<template>
  <section class="mt-12 border-t border-neutral-300" v-if="sharedUsages?.length">
    <template v-for="(usage, index) in sharedUsages" :key="index">
      <AccordionItem
        :title="getAccordionTitle(usage, index)"
        :description="getAccordionDescription(index)"
        :open="activeAccordionKey === getAccordionKey(index)"
        :controlledOpen="true"
        @toggle="onAccordionToggle(index, $event)">
        <ComplexitySliderInput
          :modelValue="getCorrectionForIndex(index)"
          :usageIndex="index"
          :role="role"
          :resultMinutes="getBreakdown(index).result"
          :legacyFactor="getBreakdown(index).override"
          @update:modelValue="onCorrectionUpdate(index, $event)" />
      </AccordionItem>
    </template>
  </section>
  <section v-else class="my-24 core-alert-zone">
    {{ $t('project.setup.sections.complexity.hint.disabled') }}
  </section>
</template>

<script
  lang="ts"
  setup
  generic="T extends { buildingCost: number; constructionCost: number; usageCorrections: UsageCorrection[] }">
import { computed, ref, toRef, type PropType } from 'vue'
import { useI18n } from 'vue-i18n'

import type { SharedUsageItem, UsageCorrection } from '@/schemas/general/types'

import { formatArea } from '@/helpers/format'
import { deepClone } from '@/helpers/objects_arrays'

import AccordionItem from '@/components/partials/layout/AccordionItem.vue'
import ComplexitySliderInput from '@/components/partials/inputs/ComplexitySliderInput.vue'
import { useWidgetText } from '@/helpers/useWidgetText'
import { getUsageDisplayTitle } from '@/helpers/usage-display'
import { useContentStore } from '@/stores/content'
import type { Role } from '@/stores/projectCenter/useCalculatorForRole'

export interface SetupCalculator {
  getMinutesPerSquareMeterForUsageAtIndex: (index: number) => number
  getMinutesPerSquareMeterForUsageAtIndexIgnoringManualComplexity: (index: number) => number
  getComplexityForUsageAtIndex: (index: number) => number
}

const props = defineProps<{
  role: Role
  setupCalculator: SetupCalculator
  sharedUsages: SharedUsageItem[]
  quantityArea: number
}>()
const { t } = useI18n()
const { wt } = useWidgetText('widget-texts-project-general', toRef(props, 'role'))
const contentStore = useContentStore()

const modelValue = defineModel({ type: Object as PropType<T> })

const activeAccordionKey = ref<string | null>(null)

// Default correction template
const defaultCorrection = (index: number): UsageCorrection => ({
  usageIndex: index,
  manualArea: undefined,
  manualComplexity: undefined,
  // Legacy coordinate fields (kept for backward compatibility)
  x1: null,
  y1: null,
  x2: null,
  y2: null,
  x3: null,
  y3: null,
  x4: null,
  y4: null,
  problem1: undefined,
  problem2: undefined,
  problem3: undefined,
  problem4: undefined,
  // New slider fields (default 1.0)
  sliderCreative: 1.0,
  sliderTechnical: 1.0,
  sliderSpatial: 1.0,
  sliderOrganizational: 1.0,
  // Justification fields
  justificationCreative: undefined,
  justificationTechnical: undefined,
  justificationSpatial: undefined,
  justificationOrganizational: undefined,
})

function getAccordionKey(index: number): string {
  return `${props.role}:${index}`
}

function onAccordionToggle(index: number, open: boolean): void {
  activeAccordionKey.value = open ? getAccordionKey(index) : null
}

function getCorrectionForIndex(index: number): UsageCorrection {
  const existing = modelValue.value?.usageCorrections?.find((c) => c.usageIndex === index)
  if (existing) return existing
  return defaultCorrection(index)
}

function onCorrectionUpdate(index: number, correction: UsageCorrection): void {
  if (!modelValue.value) return

  const corrections = deepClone(modelValue.value.usageCorrections || [])
  const existingIndex = corrections.findIndex((c) => c.usageIndex === index)

  if (existingIndex >= 0) {
    corrections[existingIndex] = correction
  } else {
    corrections.push(correction)
  }

  modelValue.value = {
    ...modelValue.value,
    usageCorrections: corrections,
  }
}

const getAccordionTitle = computed(
  () =>
    (usage: SharedUsageItem, index: number): string =>
      getUsageDisplayTitle(
        usage as SharedUsageItem & { buildingTypeId?: string | null; buildingTypeName?: string | null },
        wt,
        {
          allUsages: props.sharedUsages as Array<
            SharedUsageItem & { buildingTypeId?: string | null; buildingTypeName?: string | null }
          >,
          index,
          resolveBuildingTypeName: contentStore.getBuildingTypeNameById,
        }
      )
)

const getAccordionDescription = computed(() => (index: number): string => {
  const usage = props.sharedUsages[index]
  if (!usage) return ''

  const area = formatArea((props.quantityArea * usage.ratio) / 100)
  const areaText = t('project.setup.sections.complexity.share_area', { share: Math.round(usage.ratio), area })
  const result = Math.round(props.setupCalculator.getMinutesPerSquareMeterForUsageAtIndex(index))
  return areaText + '<span class="mx-2">|</span>' + result + ' ' + t('general.min_per_sq_m')
})

/**
 * Resulting min/m² for the result row. A legacy VAL-156 factor override or final min/m² value
 * (projects that did not accept the segment update yet) is returned as the factor that applies.
 */
const getBreakdown = computed(() => (index: number) => {
  const correction = getCorrectionForIndex(index)
  const base = props.setupCalculator.getMinutesPerSquareMeterForUsageAtIndexIgnoringManualComplexity(index)
  const legacyFinalFactor = correction.manualComplexity && base > 0 ? correction.manualComplexity / base : null
  return {
    override: correction.complexityFactorOverride ?? legacyFinalFactor,
    result: props.setupCalculator.getMinutesPerSquareMeterForUsageAtIndex(index),
  }
})
</script>
